import { ApolloClient, InMemoryCache, HttpLink, split } from '@apollo/client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import config from './aws-config';

let apolloClient = null;

/**
 * Create Apollo Client instance with Cognito authentication
 */
export function createApolloClient(getToken) {
  // HTTP link for queries and mutations
  const httpLink = new HttpLink({
    uri: config.graphqlEndpoint,
    fetch: async (uri, options) => {
      const token = await getToken();
      return fetch(uri, {
        ...options,
        headers: {
          ...options.headers,
          Authorization: token || '',
        },
      });
    },
  });

  // WebSocket link for subscriptions
  const wsLink = new GraphQLWsLink(
    createClient({
      url: config.graphqlWsEndpoint,
      connectionParams: async () => {
        const token = await getToken();
        return {
          Authorization: token || '',
        };
      },
    })
  );

  // Split link: use WebSocket for subscriptions, HTTP for everything else
  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    wsLink,
    httpLink
  );

  const cache = new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          listContent: {
            keyArgs: ['input', ['type', 'status']],
            merge(existing, incoming, { args }) {
              // Handle pagination
              if (!existing) {
                return incoming;
              }
              
              // If it's a new query (no nextToken), replace existing
              if (!args?.input?.nextToken) {
                return incoming;
              }
              
              // Append new items
              return {
                ...incoming,
                items: [...existing.items, ...incoming.items],
              };
            },
          },
          listMedia: {
            keyArgs: ['input', ['type']],
            merge(existing, incoming, { args }) {
              if (!existing) {
                return incoming;
              }
              
              if (!args?.input?.nextToken) {
                return incoming;
              }
              
              return {
                ...incoming,
                items: [...existing.items, ...incoming.items],
              };
            },
          },
        },
      },
      Content: {
        keyFields: ['contentId'],
      },
      Media: {
        keyFields: ['mediaId'],
      },
      User: {
        keyFields: ['userId'],
      },
    },
  });

  return new ApolloClient({
    link: splitLink,
    cache,
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all',
      },
      query: {
        fetchPolicy: 'cache-first',
        errorPolicy: 'all',
      },
      mutate: {
        errorPolicy: 'all',
      },
    },
  });
}

/**
 * Get or create Apollo Client singleton
 */
export function getApolloClient(getToken) {
  if (!apolloClient) {
    apolloClient = createApolloClient(getToken);
  }
  return apolloClient;
}

/**
 * Reset Apollo Client (useful for logout)
 */
export function resetApolloClient() {
  if (apolloClient) {
    apolloClient.clearStore();
    apolloClient = null;
  }
}
